import { useSearchParams } from 'next/navigation'
import { useCallback, useRef, useState } from 'react'
import {
  BlogTypeEnum,
  BLOG_PAGE_PARAM_URL_NAME,
  CmsBlogList,
  transformBlogListQueryToBlogList,
} from '../../domain/cmsBlogList'
import { BlogListDocument } from '../../queries/messikaNewsQueries/BlogList.gql'
import { useLazyQuery } from './useLazyQuery'

const updatePageParamOnUrl = (page: number) => {
  const queryParams = new URLSearchParams()
  queryParams.set(BLOG_PAGE_PARAM_URL_NAME, page.toString())
  window.history.replaceState(window.history.state, '', `?${decodeURIComponent(queryParams.toString())}`)
}

type UseBlogListFilters = {
  blogType: BlogTypeEnum
  pageSize?: number
}

const usePageParamFromUrl = () => {
  const searchParams = useSearchParams()
  const pageParamFromUrl = Number(searchParams?.get(BLOG_PAGE_PARAM_URL_NAME))
  return pageParamFromUrl && !isNaN(pageParamFromUrl) ? pageParamFromUrl : 1
}

const useBlogList = (filters: UseBlogListFilters, initialData: CmsBlogList) => {
  const pageParamFromUrl = usePageParamFromUrl()
  const page = useRef<number>(pageParamFromUrl)
  const pageSize = filters.pageSize ?? 6
  const [blogList, setBlogList] = useState<CmsBlogList>(initialData)

  const [fetchBlogList, { loading }] = useLazyQuery(BlogListDocument)

  const fetchNextPage = useCallback(async () => {
    page.current++

    const { data } = await fetchBlogList({
      variables: {
        blogType: filters.blogType,
        page: page.current,
        pageSize,
      },
    })

    if (!data) {
      return
    }

    setBlogList({
      ...blogList,
      items: [...(blogList.items ?? []), ...(transformBlogListQueryToBlogList(data).items ?? [])],
    })

    updatePageParamOnUrl(page.current)
  }, [filters, page.current])

  return {
    isLoading: loading,
    data: blogList,
    fetchNextPage,
    isLastPage: page.current >= Math.ceil(blogList.totalItems / pageSize),
    page: page.current + 1,
  }
}

export default useBlogList
